import React, { useEffect, useState } from 'react';
import { FileWithPath, useDropzone } from 'react-dropzone';
import { getFileByUrl } from '../../../../sp-firebase/storageFB';
import ButtonDesigned from '../../Buttons/ButtonDesigned';
import { ViewDocumentOverlay } from '../../ViewDocumentOverlay';
import { AttachFileErrorScreen } from '../ErrorComponents/uploadFileErrorScreen';
import { DEFAULT_APPROVED_FILE_FORMATS, DEFAULT_MAX_FILE_SIZE, uploadErrorType } from '../fileUploadUtils';
import { PendingOverlayScreen } from '../PendingOverlay';

interface DocUploadPanelProps {
  uploadedDocumentPath: string | null | undefined;
  onDeleteDocument: () => void;
  onUploadNewDocument: (_documentToAdd: any) => void;
  className?: string;
  uploadTitle?: string;
  maxFileSizeInMb?: number;
  fileFormatAllowed?: string[];
}

const DocUploadPanel: React.FC<DocUploadPanelProps> = ({
  onDeleteDocument,
  onUploadNewDocument,
  uploadedDocumentPath,
  className,
  uploadTitle = 'Upload Signed Contract',
  maxFileSizeInMb = DEFAULT_MAX_FILE_SIZE,
  fileFormatAllowed = DEFAULT_APPROVED_FILE_FORMATS,
}) => {
  const [showOverlay, setShowOverlay] = useState(false);
  const [fileUrl, setFileUrl] = useState('');
  const [isUploadError, setIsUploadError] = useState<{
    value: boolean;
    errorType: uploadErrorType;
  }>({
    value: false,
    errorType: 'none',
  });
  const [showPendingScreen, setShowPendingScreen] = useState(false);

  useEffect(() => {
    const fetchDocument = async () => {
      if (uploadedDocumentPath) {
        const docUrl = await getFileByUrl(uploadedDocumentPath);
        if (typeof docUrl === 'string') {
          setFileUrl(docUrl);
        }
      }
    };
    fetchDocument();
  }, [uploadedDocumentPath]);

  const getFileName = (fullPath: string) => {
    const parts = fullPath.split('/');
    return parts[parts.length - 1];
  };

  const dropzoneOrderConfig = {
    disabled: !!uploadedDocumentPath,
    onDrop: async (acceptedFiles: FileWithPath[]) => {
      const invalidFormatFiles = acceptedFiles.some((file: FileWithPath) => {
        return !fileFormatAllowed.includes(file.type);
      });

      const oversizedFiles = acceptedFiles.some((file: FileWithPath) => file.size > maxFileSizeInMb * 1024 * 1024); // 20MB

      if (invalidFormatFiles) {
        setIsUploadError({ errorType: 'format', value: true });
        console.error('One or more files have an invalid format.');
        return;
      }

      if (oversizedFiles) {
        setIsUploadError({ errorType: 'size', value: true });
        console.error('One or more files exceed the size limit.');
        return;
      }

      try {
        uploadedDocumentPath && onDeleteDocument();
        await onUploadNewDocument(acceptedFiles);
        setIsUploadError({ errorType: 'none', value: false });
        return;
      } catch (error) {
        setIsUploadError({ errorType: 'upload', value: true });
        console.error('Error uploading file:', error);
      }
    },
  };

  const { getRootProps: getRootProps1, getInputProps: getInputProps1 } = useDropzone(dropzoneOrderConfig);

  return (
    <>
      <div
        {...getRootProps1({
          className: `flex max-w-max border-dashed border-1 rounded border-gray-700/40 p-[0.3rem] ${className} `,
        })}
      >
        <input {...getInputProps1({ className: ' ' })} />
        {showOverlay && (
          <ViewDocumentOverlay
            fileUrl={fileUrl}
            uploadedDocumentPath={uploadedDocumentPath || '1'}
            setShowOverlay={setShowOverlay}
          />
        )}
        {uploadedDocumentPath ? (
          <div className="flex">
            <div className="flex bg-gray-50 p-[0.3rem]">
              <img src={`/assets/icons/flat/doc.svg`} alt=""></img>
              <p className="m-0 text-xs rounded p-[0.25rem] text-gray-700 opacity-80">
                {getFileName(uploadedDocumentPath)}
              </p>
              <button title="remove current document">
                <img
                  className="h-[0.75rem] w-[0.75rem]"
                  src={`/assets/icons/flat/delete.svg`}
                  onClick={onDeleteDocument}
                  alt=""
                ></img>
              </button>
            </div>
            <ButtonDesigned
              type="button"
              onButtonClick={(e: any) => {
                e.preventDefault();
                setShowOverlay(true);
              }}
              style="outline"
              className="ms-[0.25rem]"
            >
              View
            </ButtonDesigned>
          </div>
        ) : (
          <div className="flex">
            <img className="h-7 w-7" src={`/assets/icons/colored/upload.svg`} alt="" />
            <p className="m-0 text-xs rounded p-[0.5rem] text-gray-700 opacity-80">{uploadTitle}</p>
            <ButtonDesigned type="button" onButtonClick={() => {}} style="outline">
              Browse
            </ButtonDesigned>
          </div>
        )}
      </div>
      {isUploadError.value && (
        <AttachFileErrorScreen
          errorType={isUploadError.errorType}
          setDisplayOverlay={(value: boolean) => setIsUploadError({ errorType: 'none', value: value })}
        />
      )}
      {showPendingScreen && (
        <PendingOverlayScreen
          setDisplayOverlay={(value: boolean) => setShowPendingScreen(value)}
        ></PendingOverlayScreen>
      )}
    </>
  );
};

export default DocUploadPanel;
